import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useFormik } from "formik";
import * as Yup from "yup";
import bcrypt from "bcryptjs";
import { useQuery } from "react-query";
import store from "store2";
import { signIn } from "next-auth/react";
import { KNThemeError, KNThemeLoading } from "@/kyna/ui/toaster/KNTheme";

import toast from "react-hot-toast";

import { KTGet, KTPost } from "@/kyna/lib/db";
import { UserRoleDefaults } from "@/kyna/lib/StandardReferences";
import AppLogo from "../../../../public/assets/img/app-logo.png";
import DashAppLogo from "../../../../public/assets/img/dash-app-logo.svg";
import ServiceIcon from "../../../../public/assets/img/SVGServices01.svg";

import InputForm from "../../../forms/InputForm";
import NavigationBookingButton from "@/kyna/ui/button/NavigationBookingButton";
import KNModal from "@/kyna/ui/modal/KNModal";
import SlotFullModal from "./modules/SlotFullModal";
import KNToaster from "@/kyna/ui/toaster/KNToaster";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/solid";

const AccountTab = () => {
  const router = useRouter();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [response, setResponse] = useState({});
  const [isSlotFullModalOpen, setIsSlotFullModalOpen] = useState(false);
  const [submitIsLoading, setSubmitIsLoading] = useState(false);
  const [account, setAccount] = useState({
    username: "",
    password: "",
  });

  const [checkboxMessage, setCheckboxMessage] = useState(null);
  const [checkboxMessageType, setCheckboxMessageType] = useState(null);

  const [Password, setPassword] = useState("password");
  const [showPassword, setShowPassword] = useState("password");
  const togglePassword = () => {
    if (Password === "password") {
      setPassword("text");
      return;
    }
    setPassword("password");
  };

  const transaction_no = store.get("transaction_no");
  const userSession = store.get("UserSession");

  const pendingBooking = async () => {
    setSubmitIsLoading(true);
    toast.loading("Submitting...", {
      id: "submit-toast",
      ...KNThemeLoading,
    });
    const response = await KTPost(
      `/api/booking/public?transaction_no=${transaction_no}`,
      {}
    );
    if (
      response?.error?.response?.status == 404 &&
      response?.error?.response?.data?.err == "Slot is full"
    ) {
      toast.dismiss("submit-toast");
      setIsSlotFullModalOpen(true);
      setSubmitIsLoading(false);
      return;
    } else if (response?.error?.response?.status == 404) {
      toast.dismiss("submit-toast");
      toast.error(`Error, ${response?.error?.response?.data?.err} `, {
        ...KNThemeError,
      });
      setSubmitIsLoading(false);
      return;
    }
    const Logs = await KTPost(`/api/activity_logs`, {
      logs: {
        title: "Booking",
        description: userSession?.result?.username + " booked an appointment! ",
        user_id: userSession?.result?.id,
        email: userSession?.result?.username,
      },
    });
    setSubmitIsLoading(false);
    toast.dismiss("submit-toast");
    router.push("/app/account/onboarding");
  };
  const logIn = async () => {
    const login = await signIn("credentials", {
      redirect: false,
      password: account.password,
      username: account.username,
    });
    if (login.ok) {
      router.push("/app/account/onboarding");
    }
  };

  const changeAccount = async () => {
    toast.loading("Submitting...", {
      id: "submit-toast",
      ...KNThemeLoading,
    });
    const response = await KTPost(
      `/api/booking/public?transaction_no=${transaction_no}`,
      {}
    );
    if (
      response?.error?.response?.status == 404 &&
      response?.error?.response?.data?.err == "Slot is full"
    ) {
      toast.dismiss("submit-toast");
      setIsSlotFullModalOpen(true);
      return;
    } else if (response?.error?.response?.status == 404) {
      toast.dismiss("submit-toast");
      toast.error(`Error, ${response?.error?.response?.data?.err} `, {
        ...KNThemeError,
      });
      return;
    }
    toast.dismiss("submit-toast");
    router.push("/account/login/");
  };

  const slotFull = () => {
    setIsSlotFullModalOpen(false);
    router.push("/booking");
  };

  const { data, isLoading, status, refetch } = useQuery("booking", () =>
    KTGet("/api/booking/public", {
      where: `transaction_no:${transaction_no}`,
    })
  );

  function strongPasswordMethod() {
    return this.test("strongPasswordTest", function (value) {
      const { path, createError } = this;
      switch (Boolean(value)) {
        case !!value && value.length < 6:
          return createError({
            path,
            message: "Password must be at least 6 characters long.",
          });
        case !/^(?=.*[a-z])/.test(value):
          return createError({
            path,
            message: "Password must include lowercase letter",
          });
        case !/^(?=.*[A-Z])/.test(value):
          return createError({
            path,
            message: "Password must include uppercase letter",
          });
        case !/^(?=.*[0-9])/.test(value):
          return createError({ path, message: "Password must include digit" });
        case !/^(?=.*[!@#\$%\^&\*])/.test(value):
          return createError({
            path,
            message: "Password must include special character",
          });
        default:
          return true;
      }
    });
  }

  Yup.addMethod(Yup.string, "strongPassword", strongPasswordMethod);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      password: "",
      agreement: "",
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .email("Email must be valid")
        .trim()
        .required("Email is required"),
      password: Yup.string()
        .required("The password field is required")
        .strongPassword(),
      agreement: Yup.boolean()
        .required(
          "Please indicate that you agree to Terms and Conditions and Privacy Policy"
        )
        .oneOf(
          [true],
          "Please indicate that you agree to Terms and Conditions and Privacy Policy"
        ),
    }),
    onSubmit: async (values) => {
      toast.loading("Submitting...", {
        id: "submit-toast",
        ...KNThemeLoading,
      });
      setSubmitIsLoading(true);
      const response = await KTPost(
        `/api/booking/public?transaction_no=${transaction_no}`,
        {}
      );
      if (
        response?.error?.response?.status == 404 &&
        response?.error?.response?.data?.err == "Slot is full"
      ) {
        toast.dismiss("submit-toast");
        setIsSlotFullModalOpen(true);
        setSubmitIsLoading(false);
        return;
      } else if (response?.error?.response?.status == 404) {
        toast.dismiss("submit-toast");
        toast.error(`Error, ${response?.error?.response?.data?.err} `, {
          ...KNThemeError,
        });
        setSubmitIsLoading(false);
        return;
      }
      let res = {};
      let finalRes = {};
      setAccount({
        username: values.username,
        password: values.password,
      });
      const hashedPassword = await bcrypt.hash(values.password, 10);
      if (data.data.result) {
        res = await KTPost("/api/users/register", {
          user: {
            account_no: "",
            username: values.username,
            password: hashedPassword,
            status: "Active",
            user_role_id: UserRoleDefaults.find((i) => i.id === 20).id, // TODO, ADD TO THE GLOBAL CONFIG FOR PUBLIC REGISTRATION
          },
          person: {
            first_name: data?.data?.result?.first_name,
            last_name: data?.data?.result?.last_name,
            contact_no: data?.data?.result?.contact_no,
            email: values.username,
            birthday: data?.data?.result?.birthdate,
            gender: data?.data?.result?.sex,
            citizenship: data?.data?.result?.citizenship,
          },
        });
      }
      setResponse(res);
      if (res?.data?.success) {
        finalRes = await KTPost(
          `/api/booking/public?transaction_no=${transaction_no}`,
          {
            book_by_id: parseInt(res?.data?.user?.id),
          }
        );
        if (finalRes?.data?.success) {
          toast.dismiss("submit-toast");
          setIsOpenModal(true);
        } else if (
          finalRes?.error?.response?.status == 404 &&
          finalRes?.error?.response?.data?.err == "Slot is full"
        ) {
          toast.dismiss("submit-toast");
          setIsSlotFullModalOpen(true);
        } else {
          toast.dismiss("submit-toast");
          toast.error("Error Occured, Please try again", {
            ...KNThemeError,
          });
        }
        const Logs = await KTPost(`/api/activity_logs`, {
          logs: {
            title: "Booking Application",
            description: values.username + " booked an appointment! ",
            email: values.username,
          },
        });
      }
      setSubmitIsLoading(false);
    },
  });

  useEffect(() => {
    formik.setFieldValue("username", data?.data?.result?.email);
  }, []);

  return (
    <>
      <section className="mx-auto">
        <div className="relative">
          <div className="relative md:fixed w-full md:w-5/12 h-full md:min-h-screen inset-0">
            <div className="bg-blue-500 h-full md:h-screen py-12 md:py-20 px-6 sm:px-14 md:px-10 lg:px-16 xl:px-20">
              <Link href="/">
                <a className="app-logo block w-full rounded-full max-w-[80px] md:max-w-[120px] h-[80px] md:h-[120px] bg-white p-2 md:p-3">
                  <Image src={AppLogo} alt="AppLogo" />
                </a>
              </Link>
              <div className="mt-8 md:mt-14">
                <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl leading-none mb-4 sm:mb-8">
                  We hope all is well!
                </h1>
                <p className="text-white text-lg md:text-xl lg:text-2xl leading-snug lg:leading-normal">
                  We are at the forefront of mental health advocacy for{" "}
                  <strong>
                    {new Date().getFullYear() -
                      new Date("January 15, 1950").getFullYear()}{" "}
                    years now
                  </strong>
                  , reaching out and changing millions of lives.
                </p>
              </div>
            </div>
          </div>

          <div className="w-full md:w-7/12 ml-auto">
            <div className="pt-8 pb-20 px-6 sm:px-14 lg:px-16 max-w-4xl mx-auto">
              <div className="flex items-center justify-end flex-wrap gap-3 mb-10">
                <div className="form-group !mb-0">
                  <Link href="/">
                    <a className="btn btn-light-gray">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        className="w-6 h-4 mr-2"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18"
                        />
                      </svg>
                      Back to home
                    </a>
                  </Link>
                </div>
                <div className="form-group !mb-0">
                  <Link href="/account/login/">
                    <a className="btn btn-primary">Login</a>
                  </Link>
                </div>
              </div>

              <h2 className="text-blue text-4xl sm:text-5xl leading-none">
                Book an appointment
              </h2>

              <p className="text-gray-600 text-lg md:text-xl lg:text-2xl leading-snug">
                We will continue to enhance individual and collective well-being
                for a mentally healthy Philippines!
              </p>

              <div className="mt-10"></div>

              <ul
                className="nav nav-tabs flex flex-row flex-wrap list-none border-b-0 pl-0"
                id="tabs-tab"
                role="tablist"
              >
                <Link href="/booking">
                  <li className="nav-item cursor-pointer" role="presentation">
                    <span
                      className="
                        nav-link
                        block
                        font-medium
                        leading-tight
                        text-gray-400
                        border-b-2 border-transparent
                        mr-8 sm:mr-14
                        hover:text-blue
                        focus:border-transparent
                        hover:border-b-blue hover:border-b-2
                        pb-4
                        "
                      id="tabs-booking-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#tabs-booking"
                      role="tab"
                      aria-controls="tabs-booking"
                      aria-selected="false"
                    >
                      Booking
                    </span>
                  </li>
                </Link>
                <Link href="/booking/patient">
                  <li className="nav-item cursor-pointer" role="presentation">
                    <span
                      className="
                        nav-link
                        block
                        font-medium
                        leading-tight
                        text-gray-400
                        border-b-2 border-transparent
                        mr-8 sm:mr-14
                        hover:text-blue
                        focus:border-transparent
                        hover:border-b-blue hover:border-b-2
                        pb-4
                        "
                      id="tabs-patient-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#tabs-patient"
                      role="tab"
                      aria-controls="tabs-patient"
                      aria-selected="true"
                    >
                      Clients / Service Users
                    </span>
                  </li>
                </Link>
                <Link href="/booking/account">
                  <li className="nav-item cursor-pointer" role="presentation">
                    <span
                      className="
                        nav-link
                        block
                        font-medium
                        leading-tight
                        text-gray-400
                        border-b-2 border-blue
                        hover:text-blue
                        focus:border-transparent
                        hover:border-b-blue hover:border-b-2
                        pb-4
                        "
                      id="tabs-account-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#tabs-account"
                      role="tab"
                      aria-controls="tabs-account"
                      aria-selected="false"
                    >
                      Account
                    </span>
                  </li>
                </Link>
              </ul>
              <div className="border-b border-gray-200 mb-10" />
              <div className="tab-content" id="tabs-tabContent">
                {userSession ? (
                  <div className="shadow-sm space-y-3 pt-5 rounded-md">
                    <p className="font-semibold">Your E-Mail</p>
                    <p className="bg-gray-200 p-3 rounded-md">
                      {userSession?.result?.username}
                    </p>
                    <p className="text-gray-400">
                      Not {userSession?.result?.person?.first_name} ?
                      <span
                        onClick={changeAccount}
                        className="text-blue-400 cursor-pointer"
                      >
                        {" "}
                        Click Here{" "}
                      </span>
                      to change account.
                    </p>

                    <div className="flex w-full justify-between items-center pt-10">
                      <Link href="/booking/patient">
                        <NavigationBookingButton className={"rotate-180"} />
                      </Link>
                      <NavigationBookingButton
                        onClick={submitIsLoading ? null : pendingBooking}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    className="tab-pane fade"
                    id="tabs-account"
                    role="tabpanel"
                    aria-labelledby="tabs-account-tab"
                  >
                    <div className="emergency-info  mt-10 py-4">
                      <p className="font-medium">
                        Create an account for your future bookings
                      </p>
                    </div>
                    <form onSubmit={formik.handleSubmit} autoComplete={"off"}>
                      <div className="grid grid-cols-2 gap-5">
                        <div className="col-span-2">
                          <InputForm
                            type="email"
                            label="Email Address"
                            labelClassName="font-semibold text-blue text-lg"
                            id="username"
                            name="username"
                            placeholder="Please enter your email address"
                            withFormik={true}
                            formik={formik}
                          />
                          <div className="help-block text-red-400 text-xs mt-2">
                            {formik.touched.username &&
                            formik.errors.username ? (
                              <div>{formik.errors.username}</div>
                            ) : null}
                          </div>
                          <div className="help-block text-red-400 text-xs mt-2">
                            {response?.data?.schemaErrors?.username ? (
                              <div>
                                {response?.data?.schemaErrors?.username}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-span-2 relative">
                          <InputForm
                            type={Password}
                            label="Password"
                            labelClassName="font-semibold text-blue text-lg"
                            id="password"
                            name="password"
                            placeholder="Please enter a password"
                            withFormik={true}
                            formik={formik}
                          />
                          <i
                            onClick={togglePassword}
                            className="absolute top-10 right-5"
                          >
                            {Password === "password" ? (
                              <EyeOffIcon
                                onClick={() => {
                                  setShowPassword("text");
                                }}
                                className="col-span-1 h-5 w-5 text-blue-500 cursor-pointer"
                              />
                            ) : (
                              <EyeIcon
                                onClick={() => {
                                  setShowPassword("password");
                                }}
                                className="col-span-1 h-5 w-5 text-blue-500 cursor-pointer"
                              />
                            )}
                          </i>
                        </div>
                      </div>
                      <div className="flex mt-2 mb-2 w-full">
                        <input
                          type="checkbox"
                          id="agreement"
                          name="agreement"
                          className="w-5"
                          onChange={(e) => {
                            formik.setFieldValue("agreement", e.target.checked);
                          }}
                        />
                        <span className=" text-sm pl-2 w-full">
                          By continuing, you agree to the{" "}
                          <span>
                            <Link href="/terms-and-conditions">
                              <a className="underline">
                                {" "}
                                Terms and Conditions{" "}
                              </a>
                            </Link>
                          </span>{" "}
                          and confirm that you have read the{" "}
                          <span>
                            <Link href="/privacy-policy">
                              <a className="underline">Privacy Policy</a>
                            </Link>
                          </span>
                        </span>
                      </div>

                      <div className="help-block text-red-400 text-xs mt-2">
                        {formik.touched.agreement && formik.errors.agreement ? (
                          <div>{formik.errors.agreement}</div>
                        ) : null}
                      </div>

                      <p className="mt-8">
                        Already have an account ?{" "}
                        <Link passHref href="/account/login">
                          <span className="text-blue underline cursor-pointer">
                            Sign in
                          </span>
                        </Link>
                      </p>
                      <div className="flex justify-between items-center mt-10">
                        <NavigationBookingButton
                          className={"rotate-180"}
                          onClick={() => {
                            router.push("/booking/patient");
                          }}
                        />
                        <NavigationBookingButton
                          type={submitIsLoading ? "button" : "submit"}
                        />
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <KNModal
          isOpen={isOpenModal}
          noFooter={true}
          bodyClass={"max-w-4xl mt-10"}
          closeModal={() => logIn()}
          body={
            <div>
              <div
                className=" z-50 justify-center items-center md:inset-0  sm:h-full"
                id="popup-modal"
              >
                <div className="relative px-4 w-full mx-auto sm:max-w-4xl h-full md:h-auto">
                  {/* <!-- Modal content --> */}
                  <div className="relative  rounded-lg">
                    {/* <!-- Modal header --> */}
                    <div className="flex justify-end p-2 hidden">
                      <button
                        type="button"
                        onClick={() => logIn()}
                        className=""
                        data-modal-toggle="popup-modal"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                      </button>
                    </div>
                    {/* <!-- Modal body --> */}
                    <div className="pb-5">
                      <div className="modal-content">
                        <div className="max-w-xs mx-auto mb-5">
                          <Image src={ServiceIcon} alt="" />
                        </div>
                        <h2 className="text-center sm:text-3xl text-2xl">
                          Thank you, {data?.data?.result?.first_name}!
                        </h2>
                        <p className="text-center sm:text-lg text-base mb-5">
                          We will send a booking confirmation to your registered
                          email address.
                        </p>

                        <div className="form-group text-center">
                          <button
                            data-modal-toggle="popup-modal"
                            type="button"
                            onClick={logIn}
                            className="btn btn-secondary rounded-full"
                          >
                            Go to my profile
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        />
        <KNModal
          isOpen={isSlotFullModalOpen}
          noFooter={true}
          bodyClass={"max-w-4xl mt-10"}
          closeModal={slotFull}
          body={<SlotFullModal slotFull={slotFull} />}
        />
        <KNToaster />
      </section>
    </>
  );
};

export default AccountTab;
